import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import { X, ChevronUp } from "lucide-react";
import { type ParsedAgentMatch } from "@/lib/agentCatalogue";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface AgentMeta {
  imageUrl?: string | null;
  role: string;
  description: string;
}

interface AgentMatchPanelProps {
  matches: ParsedAgentMatch[];
  selectedIndexes: Set<number>;
  onToggleIndex: (i: number) => void;
  agentLookup?: Record<string, AgentMeta>;
  onProceed?: (agents: Array<{ name: string; price: number }>) => void;
  onBrowseCatalogue?: () => void;
  onClose?: () => void;
  isCollapsible?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

function AgentMatchCard({
  match,
  meta,
  selected,
  onToggle,
}: {
  match: ParsedAgentMatch;
  meta?: AgentMeta;
  selected: boolean;
  onToggle: () => void;
}) {
  const imgSrc = meta?.imageUrl ? `${BASE}/api/storage${meta.imageUrl}` : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={clsx(
        "flex flex-col gap-2 rounded-xl border backdrop-blur-sm px-4 py-3 w-full transition-opacity duration-200",
        selected
          ? "border-cyan-400/40 opacity-100 bg-purple-950/40"
          : "border-cyan-400/10 opacity-40 bg-purple-950/20"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <button
            type="button"
            role="checkbox"
            aria-checked={selected}
            onClick={onToggle}
            className={clsx(
              "mt-0.5 shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
              selected
                ? "bg-cyan-400 border-cyan-400"
                : "bg-black/30 border-slate-600 hover:border-cyan-400"
            )}
          >
            {selected && (
              <svg viewBox="0 0 10 8" className="w-3 h-3 text-slate-950 fill-current" aria-hidden>
                <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>

          <div className="shrink-0 mt-0.5 w-9 h-9 rounded-lg overflow-hidden border border-cyan-400/30 bg-cyan-400/10 flex items-center justify-center">
            {imgSrc ? (
              <img src={imgSrc} alt={match.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-cyan-300 text-xs font-bold select-none">
                {match.name.slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-[15px] text-white">{match.name}</span>
              {meta?.role && (
                <span className="text-[11px] text-slate-400 font-medium leading-tight">{meta.role}</span>
              )}
            </div>
            {meta?.description && (
              <p className="text-[12px] text-slate-400 leading-relaxed mt-0.5 line-clamp-2">{meta.description}</p>
            )}
          </div>
        </div>
        <span className="shrink-0 text-[13px] font-bold text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 rounded-full px-3 py-1 leading-tight whitespace-nowrap">
          ${match.price.toLocaleString()}
        </span>
      </div>
    </motion.div>
  );
}

export function AgentMatchPanel({
  matches,
  selectedIndexes,
  onToggleIndex,
  agentLookup,
  onProceed,
  onBrowseCatalogue,
  onClose,
  isCollapsible = false,
  isExpanded = true,
  onToggleExpand,
}: AgentMatchPanelProps) {
  const selectedAgents = matches
    .filter((_, i) => selectedIndexes.has(i))
    .map((m) => ({ name: m.name, price: m.price }));

  const totalPrice = selectedAgents.reduce((sum, a) => sum + a.price, 0);

  return (
    <div className="flex flex-col h-full bg-black/80 border-l border-cyan-400/15 backdrop-blur-md shadow-xl overflow-hidden">
      {/* Drag handle — only on collapsible mobile sheet */}
      {isCollapsible && (
        <div className="shrink-0 flex justify-center pt-2 pb-0.5">
          <div className="w-8 h-1 rounded-full bg-slate-600" />
        </div>
      )}

      {/* Panel header */}
      <div
        className={clsx(
          "shrink-0 px-4 py-3 border-b border-cyan-400/15 bg-purple-950/30 flex items-center justify-between gap-2",
          isCollapsible && "cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-inset"
        )}
        onClick={isCollapsible ? onToggleExpand : undefined}
        onKeyDown={isCollapsible ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleExpand?.(); } } : undefined}
        role={isCollapsible ? "button" : undefined}
        tabIndex={isCollapsible ? 0 : undefined}
        aria-expanded={isCollapsible ? isExpanded : undefined}
      >
        <div>
          <p className="font-semibold text-[13px] text-cyan-300 leading-tight">
            {matches.length === 1 ? "Best Match" : `${matches.length} Matches Found`}
          </p>
          {isExpanded && (
            <p className="text-[11px] text-slate-400 mt-0.5">Select the agents you want to build</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isCollapsible && (
            <ChevronUp
              size={16}
              className={clsx(
                "text-cyan-400 transition-transform duration-200",
                isExpanded ? "rotate-0" : "rotate-180"
              )}
            />
          )}
          {onClose && !isCollapsible && (
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-cyan-400/10 transition-colors text-slate-400 hover:text-cyan-300"
              title="Close panel"
            >
              <X size={14} />
            </button>
          )}
          {onClose && isCollapsible && (
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-cyan-400/10 transition-colors text-slate-400 hover:text-cyan-300"
              title="Close panel"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Cards list + actions */}
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2">
        <AnimatePresence mode="popLayout">
          {matches.map((match, i) => (
            <AgentMatchCard
              key={`${match.name}-${i}`}
              match={match}
              meta={agentLookup?.[match.name]}
              selected={selectedIndexes.has(i)}
              onToggle={() => onToggleIndex(i)}
            />
          ))}
        </AnimatePresence>

        {/* Actions sit directly below the agent cards */}
        <div className="flex flex-col gap-3 pt-3">
          {selectedAgents.length > 0 && (
            <div className="flex items-center justify-between text-[12px] text-slate-400">
              <span>
                {selectedAgents.length} agent{selectedAgents.length !== 1 ? "s" : ""} selected
              </span>
              <span className="font-bold text-cyan-300">${totalPrice.toLocaleString()}</span>
            </div>
          )}
          <button
            onClick={() => selectedAgents.length > 0 && onProceed?.(selectedAgents)}
            disabled={selectedAgents.length === 0}
            className="w-full inline-flex items-center justify-center gap-2 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold text-[13px] px-5 py-2.5 rounded-full transition-colors active:scale-95"
            style={{ boxShadow: "0 0 18px rgba(0,229,255,0.35)" }}
          >
            Yes, let's proceed!
          </button>
          {onBrowseCatalogue && (
            <button
              onClick={onBrowseCatalogue}
              className="w-full inline-flex items-center justify-center gap-2 border border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10 font-semibold text-[13px] px-5 py-2.5 rounded-full transition-colors active:scale-95"
            >
              Browse more Agents
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
